// Enhanced ChattyMemoryManager with identity tracking
// Add these sections to your existing ChattyMemoryManager class

class ChattyMemoryManager {
  constructor(config = {}) {
    // Existing configuration...
    this.config = {
      summaryThreshold: config.summaryThreshold || 10,
      importantExchangesCount: config.importantExchangesCount || 3,
      recentExchangesCount: config.recentExchangesCount || 3,
      relevantExchangesCount: config.relevantExchangesCount || 2,
      maxTokens: config.maxTokens || 4000,
      useAISummarization: config.useAISummarization || false,
      deepseekEndpoint: config.deepseekEndpoint || '',
    };

    // Existing internal state...
    this.conversationHistory = "";
    this.messageVectors = [];
    this.conversationSummary = "";
    this.lastSummarizedIndex = 0;
    this.importantExchanges = [];

    // ADD THESE NEW PROPERTIES
    // Identity and personal context tracking
    this.userIdentity = {
      name: null,
      role: null,
      relationship: null,
      personalTraits: [],
      preferences: {},
      lastIdentityUpdate: 0
    };
    
    // Topic memory - keeps track of discussed topics
    this.discussedTopics = [];
    
    // Key facts memory - important information shared during conversation
    this.keyFacts = [];
    
    // Note: Name extraction from conversations removed - now using authenticated user data
    this.hasGreeted = false;
    this.lastUserInteraction = Date.now();

    this.isInitialized = false;
    
    // Auto-clear any obviously invalid identity data on initialization
    this.clearCorruptedIdentity();
  }

  resetSession() {
    this.hasGreeted = false;
    this.conversationHistory = "";
    this.messageVectors = [];
    this.conversationSummary = "";
    this.lastSummarizedIndex = 0;
    this.importantExchanges = [];
  
    // Reset identity, topics, and key facts if you want a true fresh start
    this.userIdentity = {
      name: null,
      role: null,
      relationship: null,
      personalTraits: [],
      preferences: {},
      lastIdentityUpdate: 0
    };
    this.discussedTopics = [];
    this.keyFacts = [];
  
    // Optional: Reset timestamp tracking
    this.lastUserInteraction = Date.now();
  
    console.log("Chat session has been reset.");
  }

  clearCorruptedIdentity() {
    // General cleanup - remove any names that look like common words or typos
    if (this.userIdentity.name && this.userIdentity.name.length < 2) {
      console.log(`Clearing too-short identity name: ${this.userIdentity.name}`);
      this.userIdentity.name = null;
      this.userIdentity.lastIdentityUpdate = 0;
    }
  }

  softReset() {
    this.hasGreeted = false;               // Let AI greet again naturally next time
    this.lastUserInteraction = Date.now(); // Update timestamp
    // Optionally clear lightweight temporary mood/tone
    this.conversationSummary = "";
    this.lastSummarizedIndex = 0;
    this.importantExchanges = [];
  
    console.log("Soft reset done: tone refreshed, identity preserved.");
  }
  
  
  

  // DEPRECATED: Context size adjustment moved to Memory Lambda
  adjustContextSize(query) {
    return this.config.maxTokens;
  }

  // DEPRECATED: Complexity estimation moved to Memory Lambda
  estimateComplexity(query) {
    return "medium";
  }

  // ADD THESE NEW METHODS

  detectQueryIntent(query) {
    if (!query) return 'general';
    
    const lowerQuery = query.toLowerCase();
    
    // Personal queries related to identity, relationships
    if (
      lowerQuery.includes('who am i') ||
      lowerQuery.includes('my name') ||
      lowerQuery.includes('about me') ||
      lowerQuery.includes('remember me') ||
      lowerQuery.includes('our relationship') ||
      lowerQuery.includes('do you know me')
    ) {
      return 'personal';
    }
    
    // Factual queries seeking information
    if (
      lowerQuery.includes('what is') ||
      lowerQuery.includes('how does') ||
      lowerQuery.includes('explain') ||
      lowerQuery.includes('define') ||
      lowerQuery.includes('why is') ||
      lowerQuery.includes('tell me about')
    ) {
      return 'factual';
    }
    
    // Default to general conversation
    return 'general';
  }
  
  // Extract preferences and traits (keeping these as they're useful)
  extractPreferencesAndTraits(message) {
    // Extract personal traits and preferences only
    const traitPatterns = [
      /i (like|love|enjoy|prefer) (\w+)/ig,
      /i (hate|dislike) (\w+)/ig
    ];
    
    for (const pattern of traitPatterns) {
      pattern.lastIndex = 0; // Reset regex index
      let match;
      while ((match = pattern.exec(message)) !== null) {
        if (match[2] && match[2].length > 2) {
          // Add as a preference
          if (match[1] && (match[1].toLowerCase() === 'like' || 
              match[1].toLowerCase() === 'love' || 
              match[1].toLowerCase() === 'enjoy' || 
              match[1].toLowerCase() === 'prefer')) {
            this.userIdentity.preferences[match[2].toLowerCase()] = true;
          } else if (match[1] && (match[1].toLowerCase() === 'hate' || 
                    match[1].toLowerCase() === 'dislike')) {
            this.userIdentity.preferences[match[2].toLowerCase()] = false;
          }
        }
      }
    }
  }
  // DEPRECATED: Similarity calculation moved to Memory Lambda
calculateSimilarity(text1, text2) {
  return 0;
}

  // DEPRECATED: Semantic chunking moved to Memory Lambda
  createSemanticChunks(history) {
    return [];
  }

  // DEPRECATED: Relevance calculation moved to Memory Lambda
  calculateRelevance(query, message) {
    return 0;
  }

  // DEPRECATED: Entity extraction moved to Memory Lambda  
  extractEntities(text) {
    return [];
  }

  // DEPRECATED: History compression moved to Memory Lambda
  compressHistory(history) {
    return history;
  }
  
  extractTopics(message) {
    // Simple topic extraction based on nouns and key phrases
    const topicPatterns = [
      /(?:talk about|discuss|regarding|about) (\w+)/ig,
      /(\w+) is important/ig,
      /interested in (\w+)/ig
    ];
    
    for (const pattern of topicPatterns) {
      pattern.lastIndex = 0; // Reset regex index
      let match;
      while ((match = pattern.exec(message)) !== null) {
        if (match[1] && match[1].length > 3) {
          const topic = match[1].toLowerCase();
          const commonWords = ["it", "that", "this", "these", "those", "them", "they", "the"];
          if (!commonWords.includes(topic) && !this.discussedTopics.includes(topic)) {
            this.discussedTopics.push(topic);
          }
        }
      }
    }
  }

  summarizeChunks(chunks) {
    // Simple extractive summarization
    let summary = "";
    
    // Extract key user questions
    const userQuestions = chunks
      .map(chunk => {
        const userPart = chunk.split("Assistant:")[0].trim();
        // Extract questions or commands
        const question = userPart.match(/\b[^.!?]+\?/) || userPart.match(/\b[^.!?]+\./);
        return question ? question[0].trim() : null;
      })
      .filter(Boolean);
    
    // Keep only the most important questions (at most 5)
    summary += "Key user questions:\n";
    summary += userQuestions
      .slice(-5)
      .map(q => `- ${q}`)
      .join("\n");
    
    return summary;
  }
  
  extractKeyFacts(message) {
    // Look for statements that might be important facts
    const factPatterns = [
      /i (?:have|had|own) (?:a|an) (\w+)/ig,
      /my (\w+) is (\w+)/ig,
      /i (?:am|work as) (?:a|an) (\w+)/ig
    ];
    
    for (const pattern of factPatterns) {
      pattern.lastIndex = 0; // Reset regex index
      let match;
      while ((match = pattern.exec(message)) !== null) {
        if (match.length >= 2) {
          let fact = '';
          if (match.length >= 3) {
            fact = `User's ${match[1]} is ${match[2]}`;
          } else {
            fact = `User has a ${match[1]}`;
          }
          
          if (!this.keyFacts.includes(fact)) {
            this.keyFacts.push(fact);
          }
        }
      }
    }
  }

  // MODIFY THESE EXISTING METHODS
  
  // Modify initFromMessages to extract identity from existing messages
  initFromMessages(messages) {
    let historyText = "";
    
    for (const msg of messages) {
      if (msg.role !== "user" && msg.role !== "ai") continue;
      
      const formattedText = `${msg.role === "user" ? "User" : "Assistant"}: ${msg.text}\n\n`;
      historyText += formattedText;
      
      // Store message "vectors" (just the text for our simple implementation)
      if (msg.role === "user") {
        this.messageVectors.push({
          text: formattedText,
          timestamp: new Date().getTime()
        });
        
        // Extract preferences, topics and facts (but not identity - comes from auth)
        this.extractPreferencesAndTraits(msg.text);
        this.extractTopics(msg.text);
        this.extractKeyFacts(msg.text);
      }
    }
    
    this.conversationHistory = historyText;
    
    // Generate initial summary and important exchanges
    if (messages.length > this.config.summaryThreshold) {
      this.summarizeConversation(historyText).then(summary => {
        this.conversationSummary = summary;
        this.importantExchanges = this.identifyImportantExchanges(
          historyText, 
          this.config.importantExchangesCount
        );
        this.lastSummarizedIndex = historyText.split('User: ').filter(Boolean).length;
      });
    }

    return historyText;
  }

  // Modify addUserMessage to extract identity information
  addUserMessage(text) {
    const formattedMessage = `User: ${text}\n\n`;
    this.conversationHistory += formattedMessage;
    
    this.messageVectors.push({
      text: formattedMessage,
      timestamp: new Date().getTime()
    });
  
    // ✅ Update last user interaction time
    this.lastUserInteraction = Date.now();
  
    // Extract preferences, topics and facts (but not identity - comes from auth)
    this.extractPreferencesAndTraits(text);
    this.extractTopics(text);
    this.extractKeyFacts(text);
  
    return this.conversationHistory;
  }
  

  identifyImportantExchanges(history, count) {
    if (!history || history.length === 0) return [];
  
    const exchanges = history.split(/User:|Assistant:/).filter(Boolean);
    const scoredExchanges = [];
  
    // Go through message pairs (user + assistant)
    for (let i = 0; i < exchanges.length - 1; i += 2) {
      const userMsg = exchanges[i].trim().replace(/\n+/g, ' ');
      const aiMsg = exchanges[i + 1].trim().replace(/\n+/g, ' ');
  
      let score = 0;
  
      // Higher score if user asks a question
      if (/\?/.test(userMsg)) score += 2;
  
      // Boost if keywords suggest importance (can customize)
      if (/explain|important|remember|need to know|key|difference|how does|why/i.test(userMsg)) {
        score += 2;
      }
  
      // Longer exchanges get slight bonus
      if (userMsg.length > 100) score += 1;
      if (aiMsg.length > 150) score += 1;
  
      scoredExchanges.push({
        combined: `User: ${userMsg}\nAssistant: ${aiMsg}`,
        score: score
      });
    }
  
    // Sort by score (descending) and pick top N
    const important = scoredExchanges
      .sort((a, b) => b.score - a.score)
      .slice(0, count)
      .map(e => e.combined);
  
    return important;
  }
  

  addAIMessage(text) {
    // Initialize hasGreeted if not set
    if (this.hasGreeted === undefined) this.hasGreeted = false;
  
    // Greeting filter: If already greeted, skip repeated greetings
    if (this.hasGreeted) {
      const greetingPatterns = [
        /^hi[,!.\s]/i,
        /^hello[,!.\s]/i,
        /^hey[,!.\s]/i,
        /^hey there[,!.\s]/i,
        /^hi there[,!.\s]/i,
        /^hello there[,!.\s]/i,
        /^great to chat/i,
        /^nice to see you/i,
        /^welcome back/i,
        /^how are you/i,
        /^good to see you/i,
        /^nice to talk/i
      ];
      for (const pattern of greetingPatterns) {
        if (pattern.test(text.trim())) {
         
          return this.conversationHistory; // Do not add the AI's repeated greeting
        }
      }
    }
  
    // Mark as greeted if this is the first AI greeting
    if (!this.hasGreeted) {
      const firstGreeting = /^(hi|hello|hey|hey there|hi there|hello there|great to chat|welcome back|nice to see you|good to see you)/i;
      if (firstGreeting.test(text.trim())) {
        this.hasGreeted = true;
        // console.l
        // og("First greeting detected, marking as greeted");
      }
    }
  
    const formattedMessage = `Assistant: ${text}\n\n`;
    this.conversationHistory += formattedMessage;
    return this.conversationHistory;
  }
  

  // DEPRECATED: Context optimization now handled server-side by Memory Lambda
  async getOptimizedContext(query) {
    return "";
  }
  
  // DEPRECATED: Memory tiering moved to Memory Lambda
  applyMemoryTiers(query) {
    return;
  }
  checkInactivityAndHumanize() {
    const now = Date.now();
    const secondsIdle = (now - this.lastUserInteraction) / 1000;
  
    if (secondsIdle > 1800) { // 30 min idle
      this.softReset(); // Reset tone, but keep identity
      console.log("Soft reset due to long inactivity.");
  
      // Optionally, auto-add a natural 'welcome back' message
      const humanGreeting = this.getHumanGreeting();
      this.addAIMessage(humanGreeting);
    }
  }
  generateReconnectionResponse() {
    if (this.userIdentity.name) {
      return `Hey ${this.userIdentity.name}, of course I remember you! How’ve you been lately?`;
    } else {
      return `Of course, I remember our chat. But remind me, what’s your name again?`;
    }
  }

  getHumanGreeting() {
    const greetings = [
      "Hey, you're back! Ready to continue?",
      "Welcome back! Should we pick up where we left off?",
      "Alright, where were we?",
      "I'm here — ready when you are.",
      "Let me know what’s next!"
    ];
    return greetings[Math.floor(Math.random() * greetings.length)];
  }
  generatePersonalRecallResponse() {
    let response = 'Of course, I remember you! ';
  
    if (this.userIdentity.name) {
      response += `Your name is ${this.userIdentity.name}. `;
    } else {
      response += `Hmm, I don't have your name saved yet. Mind telling me again? `;
    }
  
    // Add preferences if known
    const likes = Object.entries(this.userIdentity.preferences)
      .filter(([item, liked]) => liked)
      .map(([item]) => item);
  
    if (likes.length > 0) {
      response += `I remember you like ${likes.join(', ')}. `;
    }
  
    response += `How have you been?`;
  
    return response;
  }
  
  
  
  
  // DEPRECATED: Memory expiry processing moved to Memory Lambda
  expireOldMemories() {
    return;
  }
  
  // DEPRECATED: Memory priority calculation moved to Memory Lambda
  getMemoryPriority(msg) {
    return 1.0;
  }
  
  // DEPRECATED: Identity analysis moved to Memory Lambda
  isIdentityRelated(text) {
    return false;
  }
  
  // DEPRECATED: Topic analysis moved to Memory Lambda
  isImportantTopic(text) {
    return false;
  }
  
  // DEPRECATED: Exchange priority moved to Memory Lambda
  getExchangePriority(exchange) {
    return 0.7;
  }

    // DEPRECATED: Topical context processing moved to Memory Lambda
  getTopicalContext(query) {
    return "";
  }

  // DEPRECATED: Topic exchange analysis moved to Memory Lambda
  findExchangesAboutTopic(topic) {
    return [];
  }

  // DEPRECATED: Exchange selection moved to Memory Lambda
  selectBestExchange(exchanges) {
    return "";
  }

  // DEPRECATED: Personal context formatting moved to Memory Lambda
  getPersonalContext() {
    return "";
  }

  // DEPRECATED: Conversation summarization moved to Memory Lambda
  async summarizeConversation(history) {
    return "Summary processing moved to server-side.";
  }

  
  // DEPRECATED: Identity context formatting moved to Memory Lambda
  getIdentityContext() {
    return "";
  }
}

export default ChattyMemoryManager;